import { InfoTooltip } from "@/components/WTooltip.tsx";
import { FormField } from "@/components/forms/FormField.tsx";
import { MonthlyCostElement } from "@/components/forms/MonthlyCostField.tsx";
import { WCheckbox } from "@/components/forms/WCheckbox.tsx";
import { pricesAtom } from "@/modules/pricing/pricingQuery.ts";
import { getIpAddressPrices, type IpAddressPrices } from "@/modules/pricing/resourcePricing.ts";
import { useSuspenseQueryAtom } from "@/utils/query/useSuspenseQueryAtom.ts";
import { Trans, useLingui } from "@lingui/react/macro";
import { Controller, type Control, type FieldValues, type Path } from "react-hook-form";

export function CreateAndAssignIpField<TInputs extends FieldValues>({
    name,
    control,
    location,
}: {
    name: Path<TInputs>;
    control: Control<TInputs>;
    location: string;
}) {
    const { t } = useLingui();

    return (
        <FormField
            label={t`Public IPv4`}
            description={t`When selected, a public floating IPv4 address will be created and assigned automatically to your resource. If you do not want to have public access to your resource you can unselect the option here or remove the IP address later.`}
        >
            <Controller
                control={control}
                name={name}
                render={({ field }) => (
                    <WCheckbox
                        isDisabled={field.disabled}
                        isSelected={field.value}
                        onChange={field.onChange}
                        data-testid="createAndAssignIpCheckbox"
                    >
                        <div>
                            <div>
                                <Trans>Create a public IPv4 address for the resource</Trans>
                            </div>
                            <InlineIpCost location={location} />
                        </div>
                    </WCheckbox>
                )}
            />
        </FormField>
    );
}

function InlineIpCost({ location, billingAccountId }: { location: string; billingAccountId?: number }) {
    const priceList = useSuspenseQueryAtom(pricesAtom);
    const prices = getIpAddressPrices(priceList, location);

    const anyNotFree = [prices.assigned, prices.unassigned].some((p) => p.hourly !== 0);
    return (
        anyNotFree && (
            <div className="inline-flex gap-1">
                <MonthlyCostElement
                    price={prices.assigned}
                    billingAccountId={billingAccountId}
                    inline
                    emphasize={false}
                />
                <InfoTooltip
                    wide
                    text={
                        <div className="flex gap-0.5rem">
                            <IpAddressAllPrices prices={prices} compact billingAccountId={billingAccountId} />
                        </div>
                    }
                />
            </div>
        )
    );
}

export function IpAddressAllPrices({
    prices,
    compact,
    billingAccountId,
}: {
    prices: IpAddressPrices;
    compact?: boolean;
    billingAccountId?: number;
}) {
    const { t } = useLingui();

    return (
        <div className="flex gap-1rem">
            <MonthlyCostElement
                price={prices.assigned}
                title={`${t`Assigned`}:`}
                compact={compact}
                emphasize={!compact}
                data-testid="assignedIpCost"
                billingAccountId={billingAccountId}
            />
            <MonthlyCostElement
                price={prices.unassigned}
                title={`${t`Unassigned`}:`}
                compact={compact}
                emphasize={false}
                data-testid="unassignedIpCost"
                billingAccountId={billingAccountId}
            />
        </div>
    );
}
